import { useState } from 'react'
import { useGoals, useCreateGoal, useUpdateGoal, useDeleteGoal } from '@/hooks/use-goals'
import { useMetrics } from '@/hooks/use-metrics'
import { useUsers } from '@/hooks/use-users'
import { Modal } from '@/components/modal'
import { GoalStatusBadge } from '@/components/goal-status-badge'
import { TIMEFRAME_LABELS, OPERATOR_SYMBOLS, type Timeframe, type GoalOperator } from '@/types'

interface GoalFormData {
  metric_id: string
  owner_id: string
  timeframe: Timeframe
  is_recurring: boolean
  period_start: string
  period_end: string
  operator: GoalOperator
  target_value: number
  target_min: number
  target_max: number
}

const defaultFormData: GoalFormData = {
  metric_id: '',
  owner_id: '',
  timeframe: 'weekly',
  is_recurring: true, // Default to recurring
  period_start: '',
  period_end: '',
  operator: 'gte',
  target_value: 0,
  target_min: 0,
  target_max: 0,
}

export function AdminGoals() {
  const { data: goals, isLoading } = useGoals()
  const { data: metrics } = useMetrics()
  const { data: users } = useUsers()
  const createGoal = useCreateGoal()
  const updateGoal = useUpdateGoal()
  const deleteGoal = useDeleteGoal()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState<GoalFormData>(defaultFormData)

  const openCreate = () => {
    setEditingId(null)
    setFormData(defaultFormData)
    setIsModalOpen(true)
  }

  const openEdit = (goal: typeof goals extends (infer T)[] | undefined ? T : never) => {
    if (!goal) return
    setEditingId(goal.id)
    const isRecurring = Boolean(goal.is_recurring) || goal.period_end === null
    setFormData({
      metric_id: goal.metric_id,
      owner_id: goal.owner_id,
      timeframe: goal.timeframe as Timeframe,
      is_recurring: isRecurring,
      period_start: goal.period_start ? new Date(goal.period_start * 1000).toISOString().split('T')[0] : '',
      period_end: goal.period_end ? new Date(goal.period_end * 1000).toISOString().split('T')[0] : '',
      operator: goal.operator as GoalOperator,
      target_value: goal.target_value,
      target_min: goal.target_min || 0,
      target_max: goal.target_max || 0,
    })
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (editingId) {
      // Update existing goal - send all editable fields for full editing/reassignment
      await updateGoal.mutateAsync({
        id: editingId,
        metric_id: formData.metric_id,
        owner_id: formData.owner_id,
        timeframe: formData.timeframe,
        operator: formData.operator,
        target_value: formData.target_value,
        is_recurring: formData.is_recurring,
        ...(formData.operator === 'range' && {
          target_min: formData.target_min,
          target_max: formData.target_max,
        }),
        // Period dates - convert from Date string to Unix timestamp
        ...(formData.period_start && {
          period_start: Math.floor(new Date(formData.period_start).getTime() / 1000),
        }),
        // For non-recurring goals, include period_end
        ...(!formData.is_recurring && formData.period_end && {
          period_end: Math.floor(new Date(formData.period_end).getTime() / 1000),
        }),
        // For recurring goals, clear period_end
        ...(formData.is_recurring && { period_end: null }),
      })
    } else {
      // Create new goal
      await createGoal.mutateAsync({
        metric_id: formData.metric_id,
        owner_id: formData.owner_id,
        timeframe: formData.timeframe,
        is_recurring: formData.is_recurring,
        operator: formData.operator,
        target_value: formData.target_value,
        ...(formData.operator === 'range' && {
          target_min: formData.target_min,
          target_max: formData.target_max,
        }),
        // For recurring goals: include period_start if user specified one
        ...(formData.is_recurring && formData.period_start && {
          period_start: Math.floor(new Date(formData.period_start).getTime() / 1000),
        }),
        // For non-recurring goals: require both start and end
        ...(!formData.is_recurring && {
          period_start: Math.floor(new Date(formData.period_start).getTime() / 1000),
          period_end: Math.floor(new Date(formData.period_end).getTime() / 1000),
        }),
      })
    }
    setIsModalOpen(false)
  }

  const handleDelete = async (id: string) => {
    if (confirm('Delete this goal?')) {
      await deleteGoal.mutateAsync(id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Goals</h1>
          <p className="text-sm text-gray-500 mt-1">Set targets for your team's metrics</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary text-sm px-3 py-1.5">
          + Add Goal
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Metric</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Owner</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Timeframe</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Target</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {goals?.map((goal) => (
              <tr key={goal.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">{goal.metric_name || goal.metric_id}</td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {users?.find((u) => u.id === goal.owner_id)?.name || goal.owner_id}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{TIMEFRAME_LABELS[goal.timeframe as Timeframe]}</td>
                <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">
                  {goal.operator === 'range'
                    ? `${goal.target_min} – ${goal.target_max}`
                    : `${OPERATOR_SYMBOLS[goal.operator as GoalOperator]} ${goal.target_value}`}
                </td>
                <td className="px-6 py-4 text-center">
                  <GoalStatusBadge status={goal.status} size="sm" />
                </td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button onClick={() => openEdit(goal)} className="text-brand-teal hover:text-brand-teal/80">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(goal.id)} className="text-danger-600 hover:text-danger-700">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!goals || goals.length === 0) && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No goals defined yet. Create your first goal.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingId ? 'Edit Goal' : 'Create Goal'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Metric *</label>
              <select
                required
                value={formData.metric_id}
                onChange={(e) => setFormData({ ...formData, metric_id: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                <option value="">Select metric...</option>
                {metrics?.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner *</label>
              <select
                required
                value={formData.owner_id}
                onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                <option value="">Select owner...</option>
                {users?.filter((u) => u.role !== 'new_user').map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timeframe</label>
              <select
                value={formData.timeframe}
                onChange={(e) => setFormData({ ...formData, timeframe: e.target.value as Timeframe })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                {Object.entries(TIMEFRAME_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer mt-6">
                <input
                  type="checkbox"
                  checked={formData.is_recurring}
                  onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-brand-teal focus:ring-brand-teal"
                />
                <span className="text-sm font-medium text-gray-700">Recurring (auto-rolls each period)</span>
              </label>
            </div>
          </div>

          {formData.is_recurring ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start From <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={formData.period_start}
                onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to start from current period
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period Start</label>
                <input
                  type="date"
                  required={!formData.is_recurring}
                  value={formData.period_start}
                  onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period End</label>
                <input
                  type="date"
                  required={!formData.is_recurring}
                  value={formData.period_end}
                  onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
              <select
                value={formData.operator}
                onChange={(e) => setFormData({ ...formData, operator: e.target.value as GoalOperator })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                <option value="gte">{OPERATOR_SYMBOLS.gte} Greater than or equal</option>
                <option value="lte">{OPERATOR_SYMBOLS.lte} Less than or equal</option>
                <option value="range">{OPERATOR_SYMBOLS.range} Within range</option>
              </select>
            </div>
            {formData.operator === 'range' ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.target_min}
                    onChange={(e) => setFormData({ ...formData, target_min: parseFloat(e.target.value) })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.target_max}
                    onChange={(e) => setFormData({ ...formData, target_max: parseFloat(e.target.value) })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Value</label>
                <input
                  type="number"
                  step="any"
                  required
                  value={formData.target_value}
                  onChange={(e) => setFormData({ ...formData, target_value: parseFloat(e.target.value) })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setIsModalOpen(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={createGoal.isPending || updateGoal.isPending}>
              {editingId ? 'Save Changes' : 'Create Goal'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
